/**
 * @fileoverview Shared browser-side config fetcher with cold-start-aware retry
 * logic. Used by tenant-web, system-web, and mobile-pwa to pull runtime config
 * from their respective backend web-config endpoints.
 * @module @firesquirrel/web-config-client
 *
 * Responsibilities:
 * - Poll a web-config endpoint with fixed-interval retries until it responds.
 * - Discriminate failure types: cold-start failures retry, misconfig failures
 *   fail fast.
 * - Return parsed JSON on success, null on exhaustion.
 *
 * Dependencies:
 * - Browser fetch / AbortController (no Node-specific APIs).
 *
 * Architecture:
 * - Generic over the response shape T — each web app passes its own config type.
 * - No backoff: fixed poll interval catches readiness ASAP. See design notes.
 * - 60s ceiling (30 attempts × 2s) covers Azure Container Apps cold starts
 *   (image pull + Key Vault load typically 30-60s).
 *
 * Notes:
 * - Why no exponential backoff: backoff optimizes for the thundering-herd
 *   scenario (many clients hammering a struggling shared resource). Here we
 *   have one browser instance polling one backend. Connection-refused and 503
 *   responses are kernel/proxy-level rejects — the booting app process never
 *   sees them, so aggressive polling costs the backend nothing. Backoff would
 *   only delay the user catching readiness. Fixed 2s polling catches the
 *   backend the moment it's ready.
 * - Why failure-type discrimination: a 404 or 401 means the endpoint is
 *   misconfigured — retrying for 60s won't fix it. Failing fast lets the app
 *   fall back to env vars immediately instead of hanging.
 */

/** Options for fetchWebConfigWithRetries. */
export interface FetchWebConfigOptions {
  /** Max number of attempts. Default 30 (~60s at 2s polling). */
  maxAttempts?: number;
  /** Milliseconds between attempts. Default 2000. */
  pollIntervalMs?: number;
  /** Per-request timeout in ms. Default 3000 (safety net for hangs). */
  requestTimeoutMs?: number;
  /** Logger function; defaults to console. */
  log?: (message: string) => void;
}

/** Categorizes a fetch failure to decide retry-vs-fail-fast. */
type FailureCategory = 'retry' | 'fail-fast';

function categorizeFailure(error: unknown, status?: number): FailureCategory {
  // Network error (connection refused, DNS, abort/timeout) — likely cold start.
  if (error) return 'retry';
  // 5xx (including 503 Service Unavailable) — transient, retry.
  if (status && status >= 500) return 'retry';
  // 408 Request Timeout, 429 Too Many Requests — transient, retry.
  if (status === 408 || status === 429) return 'retry';
  // 4xx (except above) — misconfig, won't fix itself. Fail fast.
  if (status && status >= 400 && status < 500) return 'fail-fast';
  // Unknown — be conservative, retry.
  return 'retry';
}

/**
 * Fetch a web-config endpoint with cold-start-aware retry logic.
 *
 * Polls the endpoint at a fixed interval until it returns a successful response
 * or the attempt budget is exhausted. Network errors and 5xx responses trigger
 * retries (likely cold start); 4xx responses (except 408/429) fail fast
 * (likely misconfig — retrying won't help).
 *
 * @param url  Full URL of the web-config endpoint (e.g. '/api/web-config').
 * @param opts Optional tuning; defaults to 30 attempts × 2s polling × 3s timeout.
 * @returns Parsed JSON on success, null if all attempts failed or fail-fast triggered.
 *
 * @contract fetchWebConfigWithRetries
 * Purpose: Retrieve runtime config from a backend endpoint, riding out cold starts.
 * Inputs:  url (string), opts (FetchWebConfigOptions | undefined).
 * Outputs: Promise<T | null> — T on success, null on exhaustion or fail-fast.
 * Rules:   Non-blocking on failure — caller falls back to env vars when null.
 *          No backoff — fixed interval catches readiness ASAP.
 */
export async function fetchWebConfigWithRetries<T>(
  url: string,
  opts?: FetchWebConfigOptions,
): Promise<T | null> {
  const maxAttempts = opts?.maxAttempts ?? 30;
  const pollIntervalMs = opts?.pollIntervalMs ?? 2000;
  const requestTimeoutMs = opts?.requestTimeoutMs ?? 3000;
  const log = opts?.log ?? ((msg: string) => console.log(`[web-config-client] ${msg}`));

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);
    let status: number | undefined;
    let fetchError: unknown;

    try {
      // `cache` exists in the DOM lib's RequestInit but not in @types/node's
      // (this package is compiled under both browser and node tsconfigs).
      const init: RequestInit & { cache?: 'no-store' } = { cache: 'no-store', signal: controller.signal };
      const res = await fetch(url, init);
      clearTimeout(timeoutId);
      status = res.status;

      if (res.ok) {
        const data = (await res.json()) as T;
        log(`Config loaded from ${url} on attempt ${attempt}/${maxAttempts}`);
        return data;
      }
    } catch (err) {
      clearTimeout(timeoutId);
      fetchError = err;
    }

    const category = categorizeFailure(fetchError, status);

    if (category === 'fail-fast') {
      log(`Config fetch failed with status ${status} — fail-fast (misconfig, not retrying)`);
      return null;
    }

    // Retry-able failure
    const errorDetail = fetchError
      ? (fetchError as Error).message
      : `HTTP ${status}`;
    if (attempt < maxAttempts) {
      log(`Attempt ${attempt}/${maxAttempts} failed (${errorDetail}) — retrying in ${pollIntervalMs / 1000}s`);
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    } else {
      log(`Attempt ${attempt}/${maxAttempts} failed (${errorDetail}) — exhausted, falling back`);
    }
  }

  return null;
}
